import React, { useEffect, useState } from "react";
import { getProfile } from "./apiProfile";
import { CircularProgress } from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import LogoutIcon from "@mui/icons-material/Logout";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import BadgeIcon from "@mui/icons-material/Badge";
import WcIcon from "@mui/icons-material/Wc";
import CakeIcon from "@mui/icons-material/Cake";
import HomeIcon from "@mui/icons-material/Home";
import BusinessIcon from "@mui/icons-material/Business";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import Preloader from "./components/Preloader";
import { handleLogout } from "./utils/utils";
import { useUserProfile } from "./api/hooks/useUser";

export default function Profile({ setSidebarOpen }) {
  const [profile, setProfile] = useState(null);
  const [wallet, setWallet] = useState(null);
  const [error, setError] = useState("");

  const { data: profileData, isPending } = useUserProfile();

  useEffect(() => {
    if (!profileData) return;

    setProfile(profileData.data); // handle both wrapped and direct

    // Use the detailed profile object from API
    setWallet(profileData.data.getPrimaryWallet);
  }, [profileData]);

  let user = null;
  try {
    user = JSON.parse(localStorage.getItem("user"));
  } catch {}

  // Helper to safely render any field that might be an object
  function safeRender(field, fallback = "Not provided") {
    if (typeof field === "object" && field !== null) {
      return field.label || field.slug || field.name || field.id || fallback;
    }
    return field || fallback;
  }

  // Helper to safely render boolean fields
  function safeRenderBoolean(field, trueText = "Yes", falseText = "No") {
    if (typeof field === "object" && field !== null) {
      return field.value ? trueText : falseText;
    }
    return field ? trueText : falseText;
  }

  if (isPending)
    return <Preloader fullScreen={true} message="Loading your profile..." />;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 px-4 sm:px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3 sm:gap-4">
            <button
              onClick={() => setSidebarOpen && setSidebarOpen(true)}
              className="block xl:hidden text-gray-600 hover:text-gray-900 p-1"
            >
              <MenuIcon />
            </button>
            <div className="min-w-0 flex-1">
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900 truncate">
                Profile
              </h1>
              <p className="text-sm text-gray-600 truncate">
                Manage your account information
              </p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="text-gray-600 hover:text-red-600 transition p-1 flex-shrink-0"
          >
            <LogoutIcon />
          </button>
        </div>
      </header>

      {/* Main Content */}
      <main className="px-4 sm:px-6 py-6 sm:py-8">
        {/* Error Messages */}
        {error && (
          <div className="w-full mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
            {error}
          </div>
        )}

        {/* Profile Header */}
        {profile && (
          <div className="w-full mb-8 flex flex-col md:flex-row items-center gap-6 p-6 rounded-lg bg-white border border-gray-200 shadow-sm">
            <div className="relative">
              <div className="w-24 h-24 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-full flex items-center justify-center shadow-lg">
                <span className="text-3xl font-bold text-white">
                  {safeRender(profile.first_name)?.[0] ||
                    safeRender(profile.full_name)?.[0] ||
                    safeRender(profile.username)?.[0] ||
                    "U"}
                </span>
              </div>
            </div>
            <div className="flex-1 text-center md:text-left">
              <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
                {safeRender(profile.full_name) ||
                  safeRender(profile.first_name) ||
                  safeRender(profile.username) ||
                  "User"}
              </h1>
              <p className="text-gray-600 text-lg">
                {safeRender(profile.email) || "No email provided"}
              </p>
              {wallet && (
                <div className="mt-3 flex items-center justify-center md:justify-start gap-2 text-gray-500">
                  <AccountBalanceWalletIcon className="text-lg" />
                  <span className="text-sm">
                    Primary Wallet: {safeRender(wallet.currency?.code) || "N/A"}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Profile Details Grid */}
        {profile && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Personal Information */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <AccountCircleIcon className="text-blue-600" />
                Personal Information
              </h2>
              <div className="space-y-4">
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <BadgeIcon className="text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-500">Full Name</p>
                    <p className="text-gray-900 font-medium">
                      {safeRender(profile.full_name) ||
                        safeRender(profile.first_name)}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <EmailIcon className="text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-500">Email Address</p>
                    <p className="text-gray-900 font-medium">
                      {safeRender(profile.email)}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <PhoneIcon className="text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-500">Phone Number</p>
                    <p className="text-gray-900 font-medium">
                      {safeRender(profile.telephone) ||
                        safeRender(profile.phone_number)}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <WcIcon className="text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-500">Gender</p>
                    <p className="text-gray-900 font-medium">
                      {safeRender(profile.gender, "Not specified")}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <CakeIcon className="text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-500">Date of Birth</p>
                    <p className="text-gray-900 font-medium">
                      {safeRender(profile.date_of_birth) ||
                        safeRender(profile.dob)}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Address Information */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <HomeIcon className="text-blue-600" />
                Address Information
              </h2>
              <div className="space-y-4">
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <HomeIcon className="text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-500">Address</p>
                    <p className="text-gray-900 font-medium">
                      {safeRender(profile.address)}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <BusinessIcon className="text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-500">City</p>
                    <p className="text-gray-900 font-medium">
                      {safeRender(profile.city)}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <BusinessIcon className="text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-500">State/Province</p>
                    <p className="text-gray-900 font-medium">
                      {safeRender(profile.state) ||
                        safeRender(profile.province)}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <BusinessIcon className="text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-500">Country</p>
                    <p className="text-gray-900 font-medium">
                      {safeRender(profile.country)}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <BusinessIcon className="text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-500">Postal Code</p>
                    <p className="text-gray-900 font-medium">
                      {safeRender(profile.postal_code) ||
                        safeRender(profile.zip_code)}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
